import React from "react";
import Header from "../components/Header";
import Footer from "../components/Footer";

export default function Terms() {
  return (
    <div className="min-h-screen flex flex-col bg-background" data-testid="terms-page">
      <Header />
      <main className="flex-1">
        <section className="bg-primary text-white">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 py-12 md:py-14">
            <p className="text-accent text-sm font-semibold tracking-wider uppercase mb-3">Legal</p>
            <h1 className="font-display text-4xl md:text-5xl font-bold">Terms of Service</h1>
            <p className="text-white/70 mt-3 text-sm">Last updated: January 2026</p>
          </div>
        </section>

        <article className="max-w-4xl mx-auto px-4 sm:px-6 py-10 md:py-14 prose prose-slate max-w-none">
          <p className="text-ink/80 leading-relaxed">
            Welcome to The Pataudi. By using this website (the &quot;Service&quot;), you agree to these Terms of Service.
            If you do not agree, please do not use the Service.
          </p>

          <h2 className="font-display text-2xl font-bold text-ink mt-8 mb-3">1. Who can use The Pataudi</h2>
          <p className="text-ink/80 leading-relaxed">
            You must be at least 18 years old and able to enter into a legally binding contract under Indian law. You agree to provide accurate
            information when registering and to keep your account credentials safe.
          </p>

          <h2 className="font-display text-2xl font-bold text-ink mt-8 mb-3">2. Accounts</h2>
          <ul className="list-disc pl-6 text-ink/80 space-y-1.5">
            <li>You are responsible for everything that happens under your account.</li>
            <li>One account per person. Creating duplicate accounts to abuse the credit system may result in suspension.</li>
            <li>You agree to notify us immediately if you suspect unauthorised access.</li>
          </ul>

          <h2 className="font-display text-2xl font-bold text-ink mt-8 mb-3">3. Listings</h2>
          <ul className="list-disc pl-6 text-ink/80 space-y-1.5">
            <li>Listings must describe a real property, vehicle, business, or service that the seller has the right to offer.</li>
            <li>Photos must belong to the seller or be used with permission.</li>
            <li>Prohibited content includes: illegal goods or services, weapons, stolen items, fake or duplicate listings, misleading prices, and any content that violates Indian law.</li>
            <li>We may remove or edit any listing that breaks these rules without prior notice.</li>
            <li>Sellers can edit or delete their own listings from their dashboard at any time.</li>
          </ul>

          <h2 className="font-display text-2xl font-bold text-ink mt-8 mb-3">4. Credit &amp; subscription system</h2>
          <ul className="list-disc pl-6 text-ink/80 space-y-1.5">
            <li>Browsing is free. Posting listings is free.</li>
            <li>Unlocking a seller&apos;s contact details consumes <strong>one credit per listing</strong>. Once a listing is unlocked it remains permanently visible to that user without further charge.</li>
            <li>New users receive a small number of welcome credits at sign-up.</li>
            <li>Additional credits can be purchased through subscription plans available on the pricing page.</li>
            <li>Credits are non-refundable once used to unlock a contact.</li>
            <li>Unused credits do not expire unless your account is suspended for violation of these Terms.</li>
          </ul>

          <h2 className="font-display text-2xl font-bold text-ink mt-8 mb-3">5. Payments &amp; refunds</h2>
          <p className="text-ink/80 leading-relaxed">
            All payments are processed by Razorpay. Subscription plans are billed in advance.
            Refund requests must be raised within <strong>48 hours</strong> of payment, before any credits from that purchase are consumed.
            To request a refund, contact <a href="mailto:sales@thepataudi.com" className="text-primary underline">sales@thepataudi.com</a> or call +91 9467458749.
          </p>

          <h2 className="font-display text-2xl font-bold text-ink mt-8 mb-3">6. Buyer–seller interactions</h2>
          <p className="text-ink/80 leading-relaxed">
            The Pataudi is a discovery platform. We connect buyers and sellers but we are not a party to any actual transaction between them.
            Verify the listing, the goods, the documents, and the seller&apos;s identity yourself before exchanging money. Always meet in safe,
            public places and use traceable payment methods. We are not liable for any loss, fraud, or dispute arising between you and a seller or buyer.
          </p>

          <h2 className="font-display text-2xl font-bold text-ink mt-8 mb-3">7. Prohibited conduct</h2>
          <p className="text-ink/80 leading-relaxed">You agree not to:</p>
          <ul className="list-disc pl-6 text-ink/80 space-y-1.5 mt-2">
            <li>Scrape, copy, or republish listings from The Pataudi.</li>
            <li>Use the platform for spam, harassment, or illegal solicitation.</li>
            <li>Attempt to bypass the credit/unlock system or any other technical safeguard.</li>
            <li>Reverse engineer, decompile, or interfere with the Service.</li>
          </ul>

          <h2 className="font-display text-2xl font-bold text-ink mt-8 mb-3">8. Content ownership</h2>
          <p className="text-ink/80 leading-relaxed">
            You retain ownership of the content you post. By posting, you grant The Pataudi a non-exclusive, royalty-free licence to display,
            reproduce, and distribute your content on the platform for as long as your listing is live.
          </p>

          <h2 className="font-display text-2xl font-bold text-ink mt-8 mb-3">9. Account suspension &amp; termination</h2>
          <p className="text-ink/80 leading-relaxed">
            We may suspend or terminate accounts that violate these Terms, abuse the credit system, post prohibited content, or attempt to defraud other users.
            You can also delete your own account at any time by writing to us.
          </p>

          <h2 className="font-display text-2xl font-bold text-ink mt-8 mb-3">10. Disclaimers</h2>
          <p className="text-ink/80 leading-relaxed">
            The Service is provided &quot;as is&quot;. We do not guarantee that listings are accurate, complete, or that the Service will be uninterrupted or error-free.
            To the maximum extent permitted by law, The Pataudi is not liable for indirect, incidental, or consequential damages arising out of your use of the Service.
          </p>

          <h2 className="font-display text-2xl font-bold text-ink mt-8 mb-3">11. Changes</h2>
          <p className="text-ink/80 leading-relaxed">
            We may update these Terms from time to time. Continued use of the Service after an update means you accept the revised Terms.
          </p>

          <h2 className="font-display text-2xl font-bold text-ink mt-8 mb-3">12. Governing law</h2>
          <p className="text-ink/80 leading-relaxed">
            These Terms are governed by the laws of India. Any dispute will be subject to the exclusive jurisdiction of the courts in Gurugram, Haryana.
          </p>

          <h2 className="font-display text-2xl font-bold text-ink mt-8 mb-3">13. Contact</h2>
          <p className="text-ink/80 leading-relaxed">
            Questions about these Terms? Email <a href="mailto:sales@thepataudi.com" className="text-primary underline">sales@thepataudi.com</a>,
            call <a href="tel:9467458749" className="text-primary underline">+91 9467458749</a>, or chat with us on{" "}
            <a href="https://wa.me/919467458749" target="_blank" rel="noopener noreferrer" className="text-primary underline">WhatsApp</a>.
          </p>
        </article>
      </main>
      <Footer />
    </div>
  );
}
